import { isNonEmptyString } from '@/api/queries/utils';
import FiltersContext from '@/context/FiltersContext/FiltersContext';
import { rehydrateFilterState } from '@/context/FiltersContext/FiltersContextProvider';
import { FilterType, FilterValue } from '@/models';
import { ActiveFilter } from '@/types/filter';
import { isFilterType } from '@/utils/filter/isFilterType';
import { isNonEmptyFilterModule } from '@/utils/filter/isNonEmptyFilterModule';
import { isRecord } from '@/utils/filter/isRecord';
import { isEqual, isString } from 'lodash-es';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetUserCountries } from '@/api/queries/country/useGetUserCountries';
import Stars from '@/components/shared/rating/Stars/Stars';
import { LOCAL_FILTERS_STORAGE_MAP } from '@/context/LocalFiltersRegistry/localFiltersKeys';
import { SESSION_STORAGE_SYNC_EVENT } from '@/hooks/useSessionStorage';
import { type FilterBarChipProps } from '@/storybook/components/FilterBar/FilterBar';
import { translateDynamicKey } from '@/types/translation';
import {
  APPLIED_FILTER_SET_KEY,
  AppliedFilterSet,
  AppliedFilterSetStorage,
} from '@/utils/filter/appliedFilterSetStorage';
import collectAllLocalFilters from '@/utils/filter/collectAllLocalFilters';
import { EXTENDED_FILTER_CONFIG } from './ActiveFilter/hooks/useFormattedGlobalFilterValue';
import { FILTER_ADDITIONAL_TEXT_MAP } from './ActiveFilter/hooks/useGetActiveFilterAdditionalText';
import styles from './ActiveFiltersContainer.module.css';
import useActiveLocalChips from './useActiveLocalChips';

/**
 * Checks that every non-empty filter value in the stored module is still
 * present in the current module. Handles both flat and channel-nested structures,
 * with multi-select (array) and single-select (string) values.
 */
const localModuleIntact = (stored: unknown, current: Record<string, unknown>): boolean => {
  if (typeof stored !== 'object' || stored === null) return true;

  for (const [filterKey, storedValue] of Object.entries(stored)) {
    const currentValue = current[filterKey];

    if (Array.isArray(storedValue) && storedValue.length > 0) {
      if (!Array.isArray(currentValue) || currentValue.length < storedValue.length) return false;
      if (!storedValue.every(storedItem => currentValue.some(currentItem => isEqual(currentItem, storedItem))))
        return false;
    } else if (isRecord(storedValue)) {
      if (!isRecord(currentValue)) return false;
      if (!localModuleIntact(storedValue, currentValue)) return false;
    } else if (isNonEmptyString(storedValue) && currentValue !== storedValue) {
      return false;
    }
  }
  return true;
};

interface UseActiveFilterBarReturn {
  chips: FilterBarChipProps[];
  detailsDialogFilter: AppliedFilterSet | null;
  isDetailsDialogOpen: boolean;
  closeDetailsDialog: () => void;
}

/**
 * Determines whether to show a single "filter set" chip or individual filter
 * chips based on whether an applied filter set exists and whether the user has
 * modified the filters since applying it.
 *
 * - Applied set, no modifications → one set chip with "Show filters" button
 * - Applied set, filters added → set chip + extra filter chips
 * - Applied set, filter removed from set → break set, show all individual chips
 * - No applied set → individual chips (default behavior)
 */
const useActiveFilterBar = (): UseActiveFilterBarReturn => {
  const { t } = useTranslation();
  const { activeFilters, handleDeleteActiveFilter, filterState, defaultFiltersState } = useContext(FiltersContext);
  const { data: userCountries } = useGetUserCountries();
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [detailsDialogFilter, setDetailsDialogFilter] = useState<AppliedFilterSet | null>(null);

  const [appliedSet, setAppliedSet] = useState<AppliedFilterSet | null>(() => AppliedFilterSetStorage.get());
  const [currentLocalFilters, setCurrentLocalFilters] =
    useState<Record<string, Record<string, unknown>>>(collectAllLocalFilters);

  const localFilterStorageKeys = useMemo(() => new Set(Object.values(LOCAL_FILTERS_STORAGE_MAP)), []);

  useEffect(() => {
    const handleSync = (e: Event) => {
      if (!(e instanceof CustomEvent)) return;
      const { key } = e.detail;
      if (key === APPLIED_FILTER_SET_KEY) {
        setAppliedSet(AppliedFilterSetStorage.get());
        return;
      }
      if (localFilterStorageKeys.has(key)) {
        setCurrentLocalFilters(prev => {
          const next = collectAllLocalFilters();
          return isEqual(prev, next) ? prev : next;
        });
      }
    };
    window.addEventListener(SESSION_STORAGE_SYNC_EVENT, handleSync);
    return () => window.removeEventListener(SESSION_STORAGE_SYNC_EVENT, handleSync);
  }, [localFilterStorageKeys]);

  // Rehydrate stored globalFilters so date values (dayjs) match the live filterState.
  const rehydratedSetFilters = useMemo(() => {
    if (!appliedSet) return null;
    return rehydrateFilterState(appliedSet.globalFilters, defaultFiltersState);
  }, [appliedSet, defaultFiltersState]);

  const handleShowFilters = useCallback(() => {
    const current = AppliedFilterSetStorage.get();
    if (current) {
      setDetailsDialogFilter(current);
      setIsDetailsDialogOpen(true);
    }
  }, []);

  const closeDetailsDialog = useCallback(() => {
    setIsDetailsDialogOpen(false);
    setDetailsDialogFilter(null);
  }, []);

  const formatFilterValue = useCallback(
    (filterKey: FilterType, value: FilterValue): { label: string; content?: ReactNode } => {
      if (isRecord(value) && 'value' in value && 'level' in value && typeof value.level === 'number') {
        const displayLabel = isString(value.label) ? value.label : String(value.value);
        return { label: displayLabel, content: <Stars value={value.level} /> };
      }

      const config = EXTENDED_FILTER_CONFIG[filterKey];
      if (config?.format) {
        const formatted = config.format({ value, t, contexts: { userCountries: userCountries ?? [] } });
        if (isString(formatted)) return { label: formatted };
        return { label: '', content: formatted };
      }
      return { label: isString(value) ? value : JSON.stringify(value) };
    },
    [t, userCountries],
  );

  const buildIndividualChips = useCallback(
    (filters: ActiveFilter[]): FilterBarChipProps[] =>
      filters.map(({ key, value }) => {
        const translationKey = FILTER_ADDITIONAL_TEXT_MAP[key];
        const additionalText = translationKey ? translateDynamicKey(t, translationKey) : null;
        const filterLabel = EXTENDED_FILTER_CONFIG[key]?.label;
        const tooltipText = filterLabel ? translateDynamicKey(t, filterLabel) : undefined;
        const formatted = formatFilterValue(key, value);

        const hasContent = formatted.content || additionalText;
        const chipContent = hasContent ? (
          <>
            {formatted.content}
            {additionalText ? <span className={styles.additionalText}>{additionalText}</span> : null}
          </>
        ) : undefined;

        return {
          'id': `${key}-${String(value)}`,
          'label': formatted.label,
          'tooltip': tooltipText,
          'content': chipContent,
          'onCloseClick': () => handleDeleteActiveFilter(key, value),
          'data-testid': `active-filter-${key}`,
        };
      }),
    [formatFilterValue, handleDeleteActiveFilter, t],
  );

  const setIntact = useMemo(() => {
    if (!appliedSet || !rehydratedSetFilters) return false;

    for (const key of Object.keys(rehydratedSetFilters)) {
      if (!isFilterType(key)) continue;
      if (!Object.hasOwn(filterState, key)) continue;
      const setValue = rehydratedSetFilters[key];
      const currentValue = filterState[key];
      const defaultValue = defaultFiltersState[key];

      if (isEqual(setValue, defaultValue)) continue;

      if (Array.isArray(setValue)) {
        if (!Array.isArray(currentValue)) return false;
        const allSetValuesPresent = setValue.every(setItem =>
          currentValue.some(currentItem => isEqual(currentItem, setItem)),
        );
        if (!allSetValuesPresent) return false;
      } else if (!isEqual(setValue, currentValue)) {
        return false;
      }
    }

    const storedLocal = appliedSet.localFilters;
    if (typeof storedLocal === 'object' && storedLocal !== null) {
      for (const [moduleKey, storedModuleFilters] of Object.entries(storedLocal)) {
        if (!isNonEmptyFilterModule(storedModuleFilters)) continue;
        const currentModuleFilters = currentLocalFilters[moduleKey];
        if (!currentModuleFilters) return false;
        if (!localModuleIntact(storedModuleFilters, currentModuleFilters)) return false;
      }
    }

    return true;
  }, [appliedSet, rehydratedSetFilters, filterState, defaultFiltersState, currentLocalFilters]);

  const activeLocalChips = useActiveLocalChips({ currentLocalFilters, appliedSet, setIntact });

  const extraFilters = useMemo((): ActiveFilter[] => {
    if (!appliedSet || !setIntact || !rehydratedSetFilters) return [];

    return activeFilters.filter(({ key, value }) => {
      const setValue = rehydratedSetFilters[key];
      if (Array.isArray(setValue)) {
        return !setValue.some(filterItem => isEqual(filterItem, value));
      }
      return !isEqual(setValue, value);
    });
  }, [appliedSet, setIntact, rehydratedSetFilters, activeFilters]);

  const chips = useMemo((): FilterBarChipProps[] => {
    if (!appliedSet || !setIntact) {
      return [...buildIndividualChips(activeFilters), ...activeLocalChips];
    }

    const setChip: FilterBarChipProps = {
      'id': `set-${appliedSet.userFilterId}`,
      'label': appliedSet.filterName ?? t('global.filters.appliedFilterSet'),
      'onButtonClick': handleShowFilters,
      'buttonLabel': t('global.filters.showFilters'),
      'data-testid': 'active-filter-set-chip',
    };

    const extraChips = buildIndividualChips(extraFilters);
    return [setChip, ...extraChips, ...activeLocalChips];
  }, [
    appliedSet,
    setIntact,
    activeFilters,
    extraFilters,
    activeLocalChips,
    buildIndividualChips,
    handleShowFilters,
    t,
  ]);

  return {
    chips,
    detailsDialogFilter,
    isDetailsDialogOpen,
    closeDetailsDialog,
  };
};

export default useActiveFilterBar;
